/**
 * HTML video player component for when the YouTube player is unavailable.
 * Some circumstances where this might be necessary:
 *
 *   - YouTube is blocked by a school's network
 *   - YouTube is blocked country-wide
 *   - Resources from YouTube are loading slowly or not at all
 */
import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {css} from "aphrodite";
import * as React from "react";
import {findDOMNode} from "react-dom";

import {markMinorConversions} from "@khan/analytics-learning-time";
import {useApolloGqlClient} from "@khan/data-gql-apollo";
import Log, {Errors} from "@khan/logging";
import {RequestInfo} from "@khan/shared-core";
import {staticUrl} from "@khan/urls";

import type {
    WithApolloProps,
    WithoutApollo,
} from "#data-access/gql/with-apollo.tsx";

import {VideoPlayerState} from "../constants.ts";
import {fetchFallbackPlayerCaptions} from "../fallback-captions/index.ts";
import type {VideoPlayerStateType} from "../types.ts";

type PublicProps = {
    // Aphrodite styles to be applied to the <video> element.
    // TODO(kevinb) apply styles to the wrapper element
    styles?: Array<any>;
    autoplay?: boolean;
    // Whether the user would like videos to be muted.
    // HACK(diedra): A null value means the data isn't ready yet.
    muteVideos: boolean | null | undefined;
    showCaptions: boolean | undefined;
    // Current playback rate as a number (e.g., 1, 1.5, 2)
    playbackRateNumber?: number;
    // Functions to update external models, etc.
    onDurationChange: (duration: number) => unknown;
    onPlayerReady: (togglePlayback: () => unknown) => unknown;
    onStateChange: (state: VideoPlayerStateType) => unknown;
    onTimeUpdate: (currentTime: number) => unknown;
    updateMuteVideosSetting: (isMuted: boolean) => void;
    updateShowCaptionsSetting: (showCaptions: boolean) => void;
    // Notify wrapper when user changes rate so it can update model + persist
    onPlaybackRateChange?: (playbackRateNumber: number) => void;
    // URL of a placeholder image for the video element.
    poster?: string;
    // URL of the video source.
    src: string;
    // The YoutubeId for the video.
    translatedYoutubeId?: string | null | undefined;
    // The start time for the video, held from previous sessions.
    startTime: number;
    // Whether the player should be tab-accessible.
    tabAccessible?: boolean;
    // Translated descriptive video title.
    title: string;
};

type DefaultProps = {
    playbackRateNumber: number;
    styles: NonNullable<PublicProps["styles"]>;
};

type Props = PublicProps & DefaultProps & WithApolloProps;

type State = {
    captionsSrc: string | null | undefined;
    playerState: VideoPlayerStateType;
    hasReceivedInitialCaptionEvent: boolean;
    lastKnownCaptionState: boolean | null;
};

class HtmlPlayer extends React.Component<Props, State> {
    // @ts-expect-error - TS2564 - Property '_videoElement' has no initializer and is not definitely assigned in the constructor.
    _videoElement: HTMLVideoElement;
    // @ts-expect-error - TS2564 - Property '_captionTrackElement' has no initializer and is not definitely assigned in the constructor.
    _captionTrackElement: HTMLTrackElement;

    static defaultProps: DefaultProps = {
        playbackRateNumber: 1.0,
        styles: [],
    };

    constructor(props: Props) {
        super(props);

        this.state = {
            captionsSrc: null,
            playerState: VideoPlayerState.UNSTARTED,
            hasReceivedInitialCaptionEvent: false,
            lastKnownCaptionState: null,
        };
    }

    componentDidMount() {
        // Ensure that we wait until componentDidMount to load the captions,
        // as it will perform a network request, which shouldn't happen until
        // mount occurs (especially during server-side render).
        this.maybeFetchCaptions();

        this.props.onStateChange(VideoPlayerState.UNSTARTED);
        this.props.onPlayerReady(this.toggle);

        this._videoElement.onerror = () => {
            // This should always be set in this callback, but, to make TypeScript
            // happy we do this check.
            if (this._videoElement.error) {
                const error = this._videoElement.error;

                const message = error.message || "fallback_player_error";
                const extras = {
                    errorCode: error.code.toString(),
                    errorMsg: message,
                    youtubeId:
                        this.props.translatedYoutubeId || "no youtube id found",
                    title: this.props.title || "no title found",
                } as const;

                Log.error(message, Errors.Internal, {
                    sentryData: {
                        tags: {
                            p1: "true",
                        },
                        contexts: {extras},
                    },
                });
                markMinorConversions([
                    {
                        id: "fallback_player_error",
                        extra: extras,
                    },
                ]);
            }
        };

        // note(matthewc): I was noticing that even though we change
        // the playbackRate when props change that sometimes it seemed
        // like the video would mount with a playbackRate of 1
        this.updatePlaybackRate(this.props.playbackRateNumber);
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        // HACK(diedra): A null muteVideos value means the data isn't available yet.
        // Hopefully this will can go away after we're getting this value from GraphQL.
        if (
            this.props.muteVideos === false &&
            prevProps.muteVideos === null &&
            this._videoElement.muted === true
        ) {
            this._videoElement.muted = false;
        }

        // Set up caption change event listener when captions become available
        if (
            prevState.captionsSrc !== this.state.captionsSrc &&
            this.state.captionsSrc
        ) {
            // Only set up event listener if textTracks has the addEventListener method
            // (this might not be available in test environments)
            if (
                this._videoElement.textTracks &&
                typeof this._videoElement.textTracks.addEventListener ===
                    "function"
            ) {
                this._videoElement.textTracks.addEventListener(
                    "change",
                    this.onCaptionSettingChange,
                    false,
                );
            }
        }

        if (prevProps.playbackRateNumber !== this.props.playbackRateNumber) {
            this.updatePlaybackRate(this.props.playbackRateNumber);
        }
    }

    componentWillUnmount() {
        // Clean up caption change event listener
        if (
            this._videoElement &&
            this._videoElement.textTracks &&
            typeof this._videoElement.textTracks.removeEventListener ===
                "function"
        ) {
            this._videoElement.textTracks.removeEventListener(
                "change",
                this.onCaptionSettingChange,
                false,
            );
        }
    }

    updatePlaybackRate(rateNumber?: number) {
        if (rateNumber != null) {
            this._videoElement.playbackRate = rateNumber;
        }
    }

    onCaptionSettingChange: () => void = () => {
        // Ignore the first track change event as it fires immediately when
        // the track is added during caption initialization and may not
        // reflect the user's actual interaction with captions
        if (!this.state.hasReceivedInitialCaptionEvent) {
            this.setState({hasReceivedInitialCaptionEvent: true});
            return;
        }

        // Don't update caption settings when video has ended (as captions data may be cleared)
        // or when video hasn't started yet
        if (
            this.state.playerState === VideoPlayerState.ENDED ||
            this.state.playerState === VideoPlayerState.UNSTARTED
        ) {
            return;
        }

        if (this._videoElement.textTracks.length > 0) {
            const track = this._videoElement.textTracks[0];
            const captionsOn = track.mode === "showing";

            // Only update the setting if the caption state has actually changed
            // from what we last processed (prevents duplicate updates)
            if (this.state.lastKnownCaptionState !== captionsOn) {
                this.setState({lastKnownCaptionState: captionsOn});
                this.props.updateShowCaptionsSetting(captionsOn);
            }
        }
    };

    handleDurationChange = ({target}: React.SyntheticEvent) => {
        if (target instanceof HTMLVideoElement) {
            this.props.onDurationChange(target.duration);
        }
    };

    handleEnded: () => void = () => {
        this.setState({playerState: VideoPlayerState.ENDED});
        this.props.onStateChange(VideoPlayerState.ENDED);
    };

    handlePlay: () => void = () => {
        this.setState({playerState: VideoPlayerState.PLAYING});
        this.props.onStateChange(VideoPlayerState.PLAYING);
    };

    handlePause: () => void = () => {
        this.setState({playerState: VideoPlayerState.PAUSED});
        this.props.onStateChange(VideoPlayerState.PAUSED);
    };

    handleTimeUpdate = ({target}: React.SyntheticEvent) => {
        if (target instanceof HTMLVideoElement) {
            this.props.onTimeUpdate(target.currentTime);
        }
    };

    handleVolumeChange = ({target}: React.SyntheticEvent) => {
        if (target instanceof HTMLVideoElement) {
            const {muteVideos, updateMuteVideosSetting} = this.props;

            if (target.muted !== muteVideos) {
                updateMuteVideosSetting(target.muted);
            }
        }
    };

    handlePlaybackRateChange = ({target}: React.SyntheticEvent) => {
        if (target instanceof HTMLVideoElement) {
            this.props.onPlaybackRateChange?.(target.playbackRate);
        }
    };

    // Public methods.
    //
    // Reasoning: consider the alternative, making this a controlled component
    // on currentTime. We'd need to differentiate between external updates
    // (clicking timestamps, etc.) and internal updates from
    // handleDurationChange. Otherwise, handleTimeUpdate would trigger itself
    // and cause jitter. To get around this, we'd need to track
    // currentTimeDirty in our wrapper state, and clear it on
    // componentDidUpdate. Or something. I think?
    //
    // Anyway, simply making this a public method and calling player.seekTo()
    // seems like the simplest choice. Forgive me, unidirectional React gods...
    seekTo: (seconds: number) => void = (seconds: number) => {
        if (this._videoElement) {
            this._videoElement.currentTime = seconds;
        }
    };

    play: () => void = () => {
        if (this._videoElement) {
            this._videoElement.play();
        }
    };

    toggle: () => void = () => {
        if (this._videoElement) {
            if (this._videoElement.paused) {
                this._videoElement.play();
            } else {
                this._videoElement.pause();
            }
        }
    };

    async maybeFetchCaptions() {
        if (this.props.translatedYoutubeId != null) {
            const captionsSrc = await fetchFallbackPlayerCaptions(
                this.props.gqlClient,
                this.props.translatedYoutubeId,
            );
            this.setState({captionsSrc});
        }
    }

    render(): React.ReactNode {
        const {
            autoplay,
            poster,
            src,
            startTime,
            tabAccessible,
            title,
            muteVideos,
            showCaptions,
        } = this.props;

        // muteVideos will be null if the profile information is still loading.
        // Let's default just to be safe.
        const defaultMuted = muteVideos === true || muteVideos === null;

        return (
            // eslint-disable-next-line jsx-a11y/media-has-caption -- TODO: Address a11y error
            <video
                autoPlay={autoplay}
                // Please note that `muted` only affects whether the video is initially
                // muted. After render, the user is solely in control of this setting.
                muted={defaultMuted}
                className={
                    "player ka-player-iframe centered-when-windowed " +
                    css(...this.props.styles)
                }
                controls
                onDurationChange={this.handleDurationChange}
                onTimeUpdate={this.handleTimeUpdate}
                onEnded={this.handleEnded}
                onPause={this.handlePause}
                onPlay={this.handlePlay}
                onVolumeChange={this.handleVolumeChange}
                onRateChange={this.handlePlaybackRateChange}
                poster={poster ? staticUrl(poster) : undefined}
                // NOTE: This looks more complicated than usual to appease Flow.
                // todo(matthewc): I think this could be simplified if
                // _videoElement was ?HTMLVideoElement (which I think is more accurate)
                ref={(node) => {
                    let element = findDOMNode(node);
                    if (!(element instanceof Element)) {
                        element = null;
                    }
                    this._videoElement = element as HTMLVideoElement;
                }}
                src={staticUrl(src && `${src}#t=${startTime}`)}
                tabIndex={tabAccessible ? 0 : -1}
                title={title}
            >
                {/* CSS Style for captions. */}
                {/* eslint-disable-next-line @khan/frontend/require-nltext-is-translated */}
                <style>{`::cue(.caption){ color: white; font-size: 19pt; }`}</style>
                {this.state.captionsSrc && (
                    <track
                        src={staticUrl(this.state.captionsSrc)}
                        kind="captions"
                        srcLang={RequestInfo.kaLocale}
                        label={t`Closed Captions`}
                        default={showCaptions}
                    />
                )}
                <p>
                    {t`It looks like your browser doesn't support embedded videos. Don't worry, you can still download it and watch it with your favorite video player!`}
                </p>
                <a href={src}>
                    <Trans>Download {title}</Trans>
                </a>
            </video>
        );
    }
}

type ExportProps = WithoutApollo<
    JSX.LibraryManagedAttributes<
        typeof HtmlPlayer,
        React.ComponentProps<typeof HtmlPlayer>
    >
>;

// NOTE(kevinb): We can't use HOCs for this component since some of its
// consumers make use of ref forwarding.  This is because almost all of
// our custom HOCs will not support ref forwarding in the future because
// there isn't a way to make the types work when wrapping a component in
// multiple HOCs.
export default React.forwardRef<HtmlPlayer, ExportProps>((props, ref) => {
    const {gqlClient} = useApolloGqlClient();

    return <HtmlPlayer {...props} gqlClient={gqlClient} ref={ref} />;
});
